import axios from 'lib/axios'
import {
  Application,
  ApplicationConnectResult,
  ApplicationManifest,
  CreateTicketPayload,
  JiraAssignableUsers,
  JiraCreateField,
  JiraOptions,
  JiraTicketProject,
  TicketCreated
} from 'models'

const basePath = '/api/v4/applications'

const manifestsPath = '/api/v4/manifests/applications'

/* The whole create body. The workspace and the acting user are added by the gateway, the way the
 * products service leaves `tenant_slug` to it. */
export interface CreateApplicationPayload {
  slug: string
  display_name: string
  description?: string
}

/* The callback's query, forwarded as the provider wrote it. */
export interface CompleteApplicationPayload {
  state: string
  code?: string | null
  error?: string | null
}

/* The filter route answers a bare array. Anything else means the request did not get through,
 * and an empty list is safer here than letting the page iterate over it. */
const applicationList = (data: any): Application[] => (Array.isArray(data) ? data : [])

/* The v4 applications API: the catalog of outbound integrations and a tenant's configured
 * instances of them. Calls are added as the application setup tickets need them. */
export const ApplicationsServiceV4 = {
  /* The catalog entry behind a slug. The filter route answers a list and a slug identifies at
   * most one manifest, so the first entry is it. */
  getManifest: async (slug: string): Promise<ApplicationManifest | undefined> => {
    const { data } = await axios.post<ApplicationManifest[]>(`${manifestsPath}/filter`, {
      manifest: { slug: { any_: [slug] } }
    })

    return data?.[0]
  },

  /* The whole catalog: an empty filter answers every manifest. */
  listManifests: async (): Promise<ApplicationManifest[]> => {
    const { data } = await axios.post(`${manifestsPath}/filter`, {})

    return Array.isArray(data) ? data : []
  },

  /* Every application the tenant still has, newest first. The setup page reads it to refuse a
   * display name already in use before the browser leaves for the provider. */
  listLiveApplications: async (): Promise<Application[]> => {
    const { data } = await axios.post(`${basePath}/filter`, {
      application: { removed: false },
      sort: 'CREATED_DESC'
    })

    return applicationList(data)
  },

  /* Creates the application and answers where to send the browser for approval. The row exists
   * from here on, which is why Cancel after this point removes rather than discards. */
  createApplication: async (payload: CreateApplicationPayload): Promise<ApplicationConnectResult> => {
    const { data } = await axios.post<ApplicationConnectResult>(basePath, payload)

    return data
  },

  /* Finishes the approval for the signed-in workspace and answers the application, whose
   * connection block says how the attempt ended. */
  completeApplication: async (payload: CompleteApplicationPayload): Promise<Application> => {
    const { data } = await axios.post<Application>(`${basePath}/complete`, payload)

    return data
  },

  /* One application with its nested connection, which is what the page reads its step from. */
  getApplication: async (id: string): Promise<Application> => {
    const { data } = await axios.get<Application>(`${basePath}/${id}`)

    return data
  },

  renameApplication: async (id: string, displayName: string): Promise<Application> => {
    const { data } = await axios.put<Application>(`${basePath}/${id}`, { display_name: displayName })

    return data
  },

  /* Cancels the grant at the provider, drops the stored tokens and hides the instance. */
  removeApplication: async (id: string): Promise<void> => {
    await axios.delete(`${basePath}/${id}`)
  },

  /* A fresh attempt on the same application and the same connection row: the instance keeps its
   * name and history, and only the authorization address is new. */
  retryApplication: async (id: string): Promise<ApplicationConnectResult> => {
    const { data } = await axios.post<ApplicationConnectResult>(`${basePath}/${id}/retry`)

    return data
  },

  listJiraProjects: async (id: string): Promise<JiraTicketProject[]> => {
    const { data } = await axios.get<JiraTicketProject[]>(`${basePath}/${id}/jira/projects`)

    return Array.isArray(data) ? data : []
  },

  listJiraIssueTypes: async (id: string, project: string): Promise<JiraOptions> => {
    const { data } = await axios.get<JiraOptions>(`${basePath}/${id}/jira/issue-types`, { params: { project } })

    return data
  },

  /* Jira's create form for one project and issue type, required and optional fields alike. */
  listJiraFields: async (id: string, project: string, issueType: string): Promise<JiraCreateField[]> => {
    const { data } = await axios.get<JiraCreateField[]>(`${basePath}/${id}/jira/fields`, {
      params: { project, issue_type: issueType }
    })

    return Array.isArray(data) ? data : []
  },

  listJiraUsers: async (id: string, project: string): Promise<JiraAssignableUsers> => {
    const { data } = await axios.get<JiraAssignableUsers>(`${basePath}/${id}/jira/users`, { params: { project } })

    return data
  },

  createJiraTicket: async (id: string, payload: CreateTicketPayload): Promise<TicketCreated> => {
    const { data } = await axios.post<TicketCreated>(`${basePath}/${id}/jira/tickets`, payload)

    return data
  }
}
